import { observable } from 'mobx';
import { PayerOnboardingPage } from '../index';
import { FormField } from '../../../WebGiving/components/form-field';
import { ValidationState, validateFields } from '../../../WebGiving/validation/view-model-validator';
import {
	createRulesFromMetadata,
	combineValidators,
	fieldValidationSuccess,
	FieldValidationFailure,
	RuleType,
	requiredValidator,
	alwaysValid,
	createValidatorFunc,
} from '../../../WebGiving/validation/validation-rules';
import { ModelMetadata } from '../../payer-onboarding-generated';
import { PayerOnboardingPageViewModelBase } from '../payer-onboarding-page-view-model';
import { Country } from '../../payer-onboarding-generated';
import { PhoneNumberUtils } from '../../../WebGiving/utils/phone-number-utils';

export class PageEnterDetailsViewModel extends PayerOnboardingPageViewModelBase {
	readonly type: PayerOnboardingPage.EnterDetails = PayerOnboardingPage.EnterDetails;

	@observable
	validationState?: ValidationState;

	readonly emailAddress: FormField<string>;
	readonly firstName: FormField<string>;
	readonly lastName: FormField<string>;
	readonly mobileNumber: FormField<string>;
	readonly mobileCountry: FormField<Country>;

	readonly shouldFocusOnMobileNumberField: boolean;
	readonly isAnonymousSignUp: boolean;

	constructor(
		firstName: string = '',
		lastName: string = '',
		emailAddress: string = '',
		mobileNumber: string = '',
		mobileCountry: Country = Country.US,
		isAnonymousSignUp: boolean = true,
	) {
		super();
		this.firstName = new FormField<string>(firstName, createRulesFromMetadata(ModelMetadata.ConfirmCodeAndUpdateDetailsRequest.FirstName));
		this.lastName = new FormField<string>(lastName, createRulesFromMetadata(ModelMetadata.ConfirmCodeAndUpdateDetailsRequest.LastName));

		this.mobileNumber = new FormField<string>(PhoneNumberUtils.format(mobileNumber, mobileCountry),
			combineValidators(createRulesFromMetadata(ModelMetadata.EnterMobileNumberRequest.MobileNumber), this.createMobilePhoneNumberValidator()));
		this.mobileNumber.updateSubject.register(this.synchronisePhoneCountryField);

		const country = PhoneNumberUtils.getRegionForNumber(mobileNumber, mobileCountry);
		this.mobileCountry = FormField.Unvalidated(typeof country === 'number' ? country : mobileCountry);

		this.shouldFocusOnMobileNumberField = !!firstName && !!lastName && !mobileNumber;

		this.isAnonymousSignUp = isAnonymousSignUp;

		const emailRequiredValidator = isAnonymousSignUp ? requiredValidator('Please enter email address') : alwaysValid();
		this.emailAddress = new FormField<string>(emailAddress,
			combineValidators(emailRequiredValidator, createRulesFromMetadata(ModelMetadata.ConfirmCodeAndUpdateDetailsRequest.Email)));
	}

	createMobilePhoneNumberValidator = () => createValidatorFunc((value: string) => {
		if (PhoneNumberUtils.isValidNumberForRegion(value, this.mobileCountry.value)) {
			return fieldValidationSuccess;
		}

		const errorMessage = ModelMetadata.EnterMobileNumberRequest.MobileNumber.validationRules
			? ModelMetadata.EnterMobileNumberRequest.MobileNumber.validationRules.length.errorMessage
			: 'That is not a recognised phone number';
		return new FieldValidationFailure(RuleType.Custom, errorMessage);
	}, RuleType.Custom)

	synchronisePhoneCountryField = (field: FormField<string>) => {
		const value = field.value;
		const newRegion = PhoneNumberUtils.getRegionForNumber(value, this.mobileCountry.value);

		if (typeof newRegion === 'number') {
			this.mobileCountry.updateValue(newRegion);
		}
	}

	validate(): ValidationState {
		return validateFields(this);
	}
}
